import { SMS_VOTE_TYPES, type Activity, type LivePayload } from "./types";

/**
 * ═══ LE VOTE PAR SMS, VU DE LA SALLE ═══
 *
 * Quand la régie ouvre la réception des SMS, l'écran de salle donne à chaque réponse de
 * l'activité à l'écran un numéro — 1, 2, 3… dans l'ordre où la salle les voit — et la
 * consigne pour voter : envoyer ce numéro au numéro de téléphone affiché. C'est le
 * serveur qui lit le numéro et compte le vote (`api/src/helpers/sms.js`, `recordSmsVote`).
 * Des chiffres, plus des lettres, depuis le 2026-09-24 : le serveur ne lit plus que ça.
 *
 * ⚠️ LES ACTIVITÉS NE CHANGENT PAS. Le numéro n'est écrit nulle part : il se déduit du
 * rang de la réponse, des deux côtés, sur le même ordre (`position`, puis `id`, celui
 * dans lequel le serveur envoie les choix). Réception fermée, l'écran est celui d'avant.
 *
 * ⚠️ RÉCEPTION OUVERTE, LE TÉLÉPHONE PASSE EN LECTURE SEULE (tâche #367). Il montre la
 * même chose que la salle — les réponses numérotées, la consigne — et plus ses boutons.
 * Deux façons de voter sur une même activité, c'était deux votes par personne : un
 * numéro de téléphone n'est rattaché à personne, le serveur ne peut pas les recouper.
 * C'est le geste de la V1 (`FO_MEETING_SMS_IS_ACTIVE` : ni sélection, ni « Valider »).
 * Le vote (`ballot`) n'est pas concerné : il ne se vote pas par SMS, il garde ses boutons.
 */

/** Le numéro de la réponse à ce rang, à partir de 1. Même règle que le serveur. */
export function choiceNumber(index: number): string {
  return String(index + 1);
}

/**
 * CE QUE LA SALLE ET LES TÉLÉPHONES MONTRENT DU VOTE PAR SMS.
 *
 * `numbers` : les réponses portent leur numéro. Elles le gardent une fois l'activité
 * close, résultats compris — c'est à son numéro qu'on retrouve la réponse qu'on a choisie.
 * Sur le téléphone, c'est aussi la lecture seule : on vote par SMS, plus par les boutons.
 *
 * `instruction` : la consigne, TANT QUE LE VOTE EST OUVERT. Une activité close n'accepte
 * plus de SMS : inviter la salle à en envoyer serait lui promettre un vote qui ne sera
 * pas compté.
 *
 * ⚠️ PAS LE VOTE (`ballot`). Il est réservé aux ayants droit, avec leur poids, et le
 * serveur refuse d'y compter un SMS : `SMS_VOTE_TYPES`, la même liste des deux côtés.
 */
export function smsVoting(live: Pick<LivePayload, "state" | "activity">): {
  numbers: boolean;
  instruction: boolean;
} {
  const activity = live.activity;
  const numbers =
    !!live.state.sms && !!activity && SMS_VOTE_TYPES.includes(activity.type);
  return { numbers, instruction: numbers && activity?.phase === "open" };
}

/**
 * ═══ CE QUE DEVIENT UN SMS QUI ARRIVE MAINTENANT, VU DE LA RÉGIE ═══
 *
 * Réception ouverte, le serveur compte un SMS comme un vote sur l'activité à l'écran,
 * si c'est un sondage ou un quiz et qu'elle est ouverte. Sinon le SMS arrive dans la
 * liste et ne compte pour rien — et RIEN NE LE DISAIT. Le 2026-09-25, un « 1 » envoyé
 * sur une activité lancée une minute plus tôt n'a pas compté, et l'organisateur en a
 * accusé le « 3 » de travers parti juste avant. Ce n'était pas lui : un SMS qui n'est
 * pas un numéro de réponse ne coûte rien au suivant (`api/tests/sms/vote.test.js`).
 *
 * Même règle que le serveur (`recordSmsVote`), sur la même activité : celle de
 * `state.activityId`, que porte la charge du direct.
 */
export function smsVoteStatus(
  activity: Pick<
    Activity,
    "type" | "title" | "content" | "phase" | "choices" | "config"
  > | null,
): { counts: boolean; message: string } {
  if (!activity) {
    return {
      counts: false,
      message: "Aucune activité à l'écran : les SMS arrivent ici, aucun ne compte comme un vote.",
    };
  }

  const name = `« ${activity.title || activity.content || "Sans titre"} »`;

  if (activity.type === "ballot") {
    return {
      counts: false,
      message:
        `${name} est un vote, réservé aux ayants droit : il ne se vote pas par SMS. ` +
        "Les SMS arrivent ici, aucun ne compte. Pour voter par SMS, lancez un sondage ou un quiz.",
    };
  }
  if (!SMS_VOTE_TYPES.includes(activity.type)) {
    return {
      counts: false,
      message: `${name} ne se vote pas par SMS : seuls un sondage ou un quiz comptent les SMS.`,
    };
  }
  if (activity.phase === "draft") {
    return {
      counts: false,
      message: `${name} n'est pas encore lancée : les SMS ne compteront qu'une fois l'activité ouverte.`,
    };
  }
  if (activity.phase !== "open") {
    return {
      counts: false,
      message: `${name} est close : les SMS qui arrivent ne comptent plus.`,
    };
  }

  const max = Math.max(1, activity.config.maxAnswers ?? 1);
  return {
    counts: true,
    message:
      `Les SMS comptent pour ${name} : un numéro de 1 à ${activity.choices.length}` +
      `${max > 1 ? `, jusqu'à ${max} dans le même SMS` : ""}.`,
  };
}
